import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface PermissionContextType {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  getUserPermissions: () => string[];
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

// Define role-based permissions
// Member role removed - will be handled by separate mobile app
const rolePermissions: { [key: string]: string[] } = {
  'Admin': [
    'view_dashboard',
    'view_enquiries', 'create_enquiry', 'edit_enquiry', 'delete_enquiry', 'convert_enquiry',
    'view_members', 'edit_member', 'delete_member', 'view_receipts', 'resend_receipt',
    'view_payments', 'add_payment', 'upgrade_membership',
    'view_plans', 'create_plan', 'edit_plan', 'delete_plan',
    'view_users', 'create_user', 'edit_user', 'delete_user', 'manage_roles',
    'view_reports', 'export_reports',
    'view_activity',
    'view_access_control'
  ],
  'Receptionist': [
    'view_dashboard',
    'view_enquiries', 'create_enquiry', 'edit_enquiry', 'convert_enquiry',
    'view_members', 'edit_member', 'view_receipts', 'resend_receipt',
    'view_payments', 'add_payment',
    'view_plans',
    'view_activity'
  ],
  'Trainer': [
    'view_dashboard',
    'view_enquiries',
    'view_members',
    'view_receipts',
    'view_activity'
  ]
};

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const getUserRole = (): string => {
    // Get user role from user object
    if (user && user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
      console.log('User role from context:', user.roles[0]);
      return user.roles[0];
    }
    console.log('No role found, no default role (Member removed for mobile app)');
    return 'Unknown';
  };

  const getUserPermissions = (): string[] => {
    const role = getUserRole();
    return rolePermissions[role] || []; // Return empty permissions if role not found
  };

  const hasPermission = (permission: string): boolean => {
    // SuperAdmin has all permissions
    if (user?.isSuperAdmin) {
      return true;
    }

    const role = getUserRole();
    
    // Admin has all permissions
    if (role === 'Admin') {
      return true;
    }

    const permissions = rolePermissions[role] || [];
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const value = {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Permission-based component wrapper
interface PermissionGuardProps {
  permission: string | string[];
  fallback?: ReactNode;
  children: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  permission, 
  fallback = null, 
  children 
}) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const hasAccess = Array.isArray(permission)
    ? hasAnyPermission(permission)
    : hasPermission(permission);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

